import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { GridColDef, GridColumnVisibilityModel, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import { useIntl } from 'react-intl';
import LanguageUtils from '../../../utils/LanguageUtils';
import TableUtils from '../../../utils/tableUtils';
import UrlConstants from '../../../constants/UrlConstants';
import CustomStringLink from '../../common/customStringLink';
import HelptextConstantsEnum from '../../../constants/helptextConstantsEnum';
import DataGridComponent from '../../common/dataGrid';
import CustomLink from '../../common/customLink';
import NumericFormat from 'react-number-format';

interface ReportHeader005ListProps {
    classes?: any;
    checkSelection?: any;
    isLoading?: boolean;
    multipleSelection?: boolean;
    componentsProps?: any;
    onPageChange?: any;
    rows?: any;
    applyRowFn?: any;
    hiddenColumns?: Array<string>;
    onSortModelChange?: any;
    columnVisibilityModel?: GridColumnVisibilityModel;
    onColumnVisibilityModelChange?: any;
    setCurrentComponentId?: any;
    disableLinks?: boolean;
}

const messages = {
    invoiceYear: LanguageUtils.createMessage('Invoice Year'),
    invoiceMonth: LanguageUtils.createMessage('Invoice Month'),
    total: LanguageUtils.createMessage('Total'),
    invoicesOk: LanguageUtils.createMessage('Invoices OK'),
    invoicesMissingInD365: LanguageUtils.createMessage('Invoices Missing in D365'),
    invoicesWrongInD365: LanguageUtils.createMessage('Invoices wrong amount in D365'),
    salesPriceCapry: LanguageUtils.createMessage('Sales Price Capri'),
    salesPriceD365: LanguageUtils.createMessage('Sales Price D365'),
    diff: LanguageUtils.createMessage('Diff')
};

const ReportHeader005List = forwardRef((props: ReportHeader005ListProps, ref: any): JSX.Element => {
    const { componentsProps, isLoading, checkSelection, onPageChange, rows, hiddenColumns, multipleSelection, onSortModelChange,
        columnVisibilityModel, onColumnVisibilityModelChange, setCurrentComponentId } = props;

    const defaultFn = (cellValues: GridRenderCellParams | GridValueGetterParams) => cellValues.row;

    const applyRowFn = props.applyRowFn || defaultFn;

    const dataGridRef = useRef();

    useImperativeHandle(ref, () => ({
        resetDataGridPage() {
            if(dataGridRef !== null && dataGridRef !== undefined && dataGridRef.current !== null && dataGridRef.current !== undefined) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                dataGridRef.current.resetPage();
            }
        }
    }));

    useEffect(() => {
        if(setCurrentComponentId !== undefined) {
            setCurrentComponentId(HelptextConstantsEnum.REPORT_ORDERS_NOT_INVOICES);
        }
    }, []);

    function generateColumns(): GridColDef[] {
        const intl = useIntl();

        return [
            {
                field: 'invoiceYear',
                headerName: intl.formatMessage(messages.invoiceYear),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.invoiceYear}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('invoiceYear', hiddenColumns)
            },
            {
                field: 'invoiceMonth',
                headerName: `${intl.formatMessage(messages.invoiceMonth)}`,
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.invoiceMonth}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('invoiceMonth', hiddenColumns)
            },
            {
                field: 'total',
                headerName: intl.formatMessage(messages.total),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: any) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.total}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('total', hiddenColumns)
            },
            {
                field: 'invoicesOK',
                headerName: intl.formatMessage(messages.invoicesOk),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: any) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.invoicesOK}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('ousStatusName', hiddenColumns)
            },
            {
                field: 'invoicesMissingInD365',
                headerName: intl.formatMessage(messages.invoicesMissingInD365),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: any) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.invoicesMissingInD365}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('invoicesMissingInD365', hiddenColumns)
            },
            {
                field: 'invoicesWrongAmountInD365',
                headerName: intl.formatMessage(messages.invoicesWrongInD365),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: any) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.invoicesWrongAmountInD365}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('invoicesWrongAmountInD365', hiddenColumns)
            },
            {
                field: 'salesPriceCapri',
                headerName: intl.formatMessage(messages.salesPriceCapry),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: any) => {
                    return <CustomLink align={'right'}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`}>
                        <NumericFormat value={applyRowFn(param)?.salesPriceCapri} displayType={'text'} thousandSeparator={true} />
                    </CustomLink>;
                },
                hide: hiddenColumns && TableUtils.hideColumn('salesPriceCapri', hiddenColumns)
            },
            {
                field: 'salesPriceD365',
                headerName: intl.formatMessage(messages.salesPriceD365),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: any) => {
                    return <CustomLink align={'right'}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`}>
                        <NumericFormat value={applyRowFn(param)?.salesPriceD365} displayType={'text'} thousandSeparator={true} />
                    </CustomLink>;
                },
                hide: hiddenColumns && TableUtils.hideColumn('salesPriceD365', hiddenColumns)
            },
            {
                field: 'diff',
                headerName: intl.formatMessage(messages.diff),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: any) => {
                    return <CustomLink align={'right'}
                        link={`/#/${UrlConstants.REPORT_LINE_005}/${applyRowFn(param)?.id}`}>
                        <NumericFormat value={applyRowFn(param)?.diff} displayType={'text'} thousandSeparator={true} />
                    </CustomLink>;
                },
                hide: hiddenColumns && TableUtils.hideColumn('diff', hiddenColumns)
            }
        ];
    }

    return (
        <DataGridComponent rows={rows} multipleSelection={multipleSelection} columns={generateColumns()}
            ref={dataGridRef} componentsProps={componentsProps} isLoading={isLoading}
            checkSelection={checkSelection} onPageChange={onPageChange} onSortModelChange={onSortModelChange}
            columnVisibilityModel={columnVisibilityModel} onColumnVisibilityModelChange={onColumnVisibilityModelChange} />
    );
});

export default ReportHeader005List;
